import { Button, Col, Divider, Form, message, Row, Typography } from "antd";
import React, { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useSmoothLoading } from "../../../../../../hooks/useSmoothLoading";
import { AppDispatch } from "../../../../../../redux/store";
import { InputField } from "../../../../../atoms/FormElement";
import { calculateTotalCharges, currencyFormat } from "../../../../../libs/utils";
import { changeFieldFormatter, changeFieldParser } from "../../../../BookingForm/components/utils";
import { getTicketByID, patchTicket } from "../../ducks/actions";
import { TicketSubCategory } from "../../types";
import { hasStatus } from "./hasStatus";

type FormData = {
  unitPrice: number;
  vat: number;
  quantity: number;
  discount: number;
  total: number;
};

type ServiceChargesFormProps = {
  onSubmitSuccess?: () => Promise<void>;
};

export const ServiceChargesForm = (props: ServiceChargesFormProps) => {
  const { onSubmitSuccess } = props;

  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, startLoading, stopLoading } = useSmoothLoading();

  const ticket = useSelector((state: any) => state.tickets.getTicketById) || {};
  const ticketSubCategory = ticket?.subCategory as TicketSubCategory;

  const isPaymentReceived = hasStatus(ticket?.ticketStatusHistory, ["pending_payment", "payment_received"]);

  const defaultValues = useMemo(
    () => ({
      unitPrice: ticketSubCategory?.unitPrice || 0,
      vat: ticketSubCategory?.vat || 0,
      total: ticketSubCategory?.total || 0,
      quantity: ticketSubCategory?.quantity || 1,
      discount: ticketSubCategory?.discount || 0,
    }),
    [ticketSubCategory]
  );

  const methods = useForm<FormData>({ defaultValues });
  const { watch, setValue, handleSubmit, reset, control } = methods;

  useEffect(() => {
    if (ticketSubCategory) {
      reset(defaultValues);
    }
  }, [ticketSubCategory, defaultValues, methods]);

  const quantity = watch("quantity");
  const discount = watch("discount");
  const total = watch("total");

  useEffect(() => {
    const total = calculateTotalCharges({
      unitPrice: ticketSubCategory?.unitPrice,
      vat: ticketSubCategory?.vat,
      quantity,
      discount,
    });

    setValue("total", total);
  }, [quantity, discount, setValue]);

  const onSubmit = async (formData: FormData) => {
    const { quantity, discount } = formData;
    try {
      startLoading();
      await patchTicket(ticket.ticketId, {
        quantity: Number(quantity),
        ...(discount > 0 && { discount: Number(discount) }),
      });
      message.success("Service charges updated successfully");
      await dispatch(getTicketByID(ticket.ticketId));

      await onSubmitSuccess?.();
    } catch (error) {
      message.error(error?.response?.data?.message || "Error occurred while updating service charges");
    } finally {
      stopLoading();
    }
  };

  return (
    <FormProvider {...methods}>
      <Form
        layout="vertical"
        style={{ width: "100%", marginTop: "1rem", marginBottom: "1rem" }}
        onFinish={handleSubmit(onSubmit)}
      >
        <Typography.Title level={4}>Service Charges</Typography.Title>

        {[
          { label: "Unit Price", value: currencyFormat(defaultValues.unitPrice) },
          { label: "VAT", value: `${defaultValues.vat}%` },
          { label: "Amount (Incl. VAT)", value: currencyFormat(total) },
        ].map(({ label, value }) => (
          <React.Fragment key={label}>
            <Row>
              <Col span={8}>
                <Typography.Text className="ag-primary" strong>
                  {label}
                </Typography.Text>
              </Col>
              <Col flex="1">
                <Typography.Text className="smallFont14 fontWeight600">{value}</Typography.Text>
              </Col>
            </Row>
            <Divider />
          </React.Fragment>
        ))}

        <Row gutter={12} align="bottom" style={{ marginTop: "1rem" }}>
          <Col flex="2">
            <InputField
              isRequired
              label="Quantity"
              fieldname="quantity"
              control={control}
              iProps={{
                type: "number",
                min: 1,
                disabled: isPaymentReceived,
                formatter: (value) => changeFieldFormatter(value, "amount"),
                parser: (value) => changeFieldParser(value, "amount"),
              }}
              formItemStyle={{ marginBottom: 0 }}
              inputStyle={{ borderRadius: 6, height: 40 }}
            />
          </Col>

          <Col flex="2">
            <InputField
              isRequired
              label="Discount (%)"
              fieldname="discount"
              control={control}
              iProps={{
                type: "number",
                min: 0,
                max: 100,
                disabled: isPaymentReceived,
                formatter: (value) => changeFieldFormatter(value, "amount"),
                parser: (value) => changeFieldParser(value, "amount"),
              }}
              formItemStyle={{ marginBottom: 0 }}
              inputStyle={{ borderRadius: 6, height: 40 }}
            />
          </Col>

          <Col flex="1">
            {!isPaymentReceived && (
              <Button
                style={{ width: "7.25rem", height: "2.5rem", borderRadius: "8px" }}
                type="primary"
                htmlType="submit"
                loading={isLoading}
              >
                Update
              </Button>
            )}
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
};
